import { ReactNode } from 'react';

import { VesselProductMessage } from '../../../../types/VesselProductMessage';
import { DynamicFilterContainer } from '../../../cdl/Filter/DynamicFilterContainer/DynamicFilterContainer';
import { FilterMultiSelectFull } from '../../../cdl/Filter/FilterMultiSelectFull/FilterMultiSelectFull';
import { FilterMultiSelectShort } from '../../../cdl/Filter/FilterMultiSelectShort/FilterMultiSelectShort';
import { FilterRadioGroup } from '../../../cdl/Filter/FilterRadioGroup/FilterRadioGroup';
import { FilterText } from '../../../cdl/Filter/FilterText/FilterText';
import { translate } from '../../../common/helpers/translate.helper';
import { useEnums } from '../../../common/hooks/useEnums';

interface VesselProductsFiltersProps {
    allEntries: VesselProductMessage[];
    children: (filteredEntries: VesselProductMessage[]) => ReactNode;
}

export const VesselProductsFilters = ({ allEntries, children }: VesselProductsFiltersProps) => {
    const { enums } = useEnums();

    const supplierGroupEnum = enums?.supplierProductGroup || {};
    const productGroupEnum = enums?.productGroup || {};

    const availableSupplierGroups = Array.from(
        new Set(allEntries.map((vesselProduct) => vesselProduct.product.supplierProductGroup))
    ).map((key) => ({ label: supplierGroupEnum[key]?.humanReadableValue ?? key, value: key }));

    const availableProductGroups = Array.from(
        new Set(allEntries.map((vesselProduct) => vesselProduct.product.group))
    ).map((key) => ({
        label: productGroupEnum[key]?.humanReadableValue ?? key,
        value: key,
    }));

    const availableProducts = Array.from(new Set(allEntries.map((vesselProduct) => vesselProduct.product.id))).map(
        (id) => {
            const product = allEntries.find((p) => p.product.id === id)!.product;
            return { label: product.name, value: id };
        }
    );

    return (
        <DynamicFilterContainer
            allEntries={allEntries}
            filters={{
                product: {
                    filterFn: (values: string[], entry: VesselProductMessage) => values.includes(entry.product.id),
                    component: (values, setValues) => (
                        <FilterMultiSelectFull
                            label={translate('vessel.products.filters.product')}
                            filterByLabel={translate('vessel.products.filters.filterByProduct')}
                            filterPlaceholder={translate('vessel.products.filters.searchForProduct')}
                            options={availableProducts}
                            values={values}
                            onChange={setValues}
                        />
                    ),
                },
                tank: {
                    filterFn: (values: boolean[], entry: VesselProductMessage) => values.includes(entry.isInTank),
                    component: (values, setValues) => (
                        <FilterRadioGroup<boolean>
                            label={translate('vessel.products.filters.tank')}
                            filterByLabel={translate('vessel.products.filters.filterByTankPresence')}
                            options={[
                                { label: translate('global.yes'), value: true },
                                { label: translate('global.no'), value: false },
                            ]}
                            value={values[0]}
                            onChange={(value) => setValues(value === undefined ? [] : [value])}
                        />
                    ),
                },
                supplierGroup: {
                    filterFn: (values: string[], entry: VesselProductMessage) =>
                        values.includes(entry.product.supplierProductGroup),
                    component: (values, setValues) => (
                        <FilterMultiSelectShort
                            label={translate('vessel.products.filters.supplierGroup')}
                            filterByLabel={translate('vessel.products.filters.filterBySupplierGroup')}
                            options={availableSupplierGroups}
                            values={values}
                            onChange={setValues}
                        />
                    ),
                },
                productGroup: {
                    filterFn: (values: string[], entry: VesselProductMessage) => values.includes(entry.product.group),
                    component: (values, setValues) => (
                        <FilterMultiSelectShort
                            label={translate('vessel.products.filters.productGroup')}
                            filterByLabel={translate('vessel.products.filters.filterByProductGroup')}
                            options={availableProductGroups}
                            values={values}
                            onChange={setValues}
                        />
                    ),
                },
                note: {
                    filterFn: (values: string[], entry: VesselProductMessage) => {
                        if (values.length === 0) {
                            return true;
                        }
                        const note = entry.note?.toLowerCase() || '';
                        return values.some((value) => note.includes(value.toLowerCase()));
                    },
                    component: (values, setValues) => (
                        <FilterText
                            label={translate('vessel.products.filters.note')}
                            filterByLabel={translate('vessel.products.filters.filterByNote')}
                            value={values[0]}
                            onChange={(value) => setValues(value ? [value] : [])}
                        />
                    ),
                },
            }}
        >
            {children}
        </DynamicFilterContainer>
    );
};
